import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Button, Steps, Alert, Checkbox } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { MALL_APPS, MALL_USER_SEGMENTS } from '../mallMockData';

const AREA_OPTIONS = [{ label: '国内', value: '1' }, { label: '海外', value: '2' }];

const checkboxGroupStyle = { display: 'flex', flexDirection: 'column', gap: 12, width: '100%' };

/**
 * 商城位运营 - 添加/编辑策略抽屉（2步向导）
 * 真实项目：views/operation/mall/components/AddStrategyDrawer.vue
 */
const MallAddStrategyDrawer = ({ open, editData, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const isEdit = !!editData;
    const isDefaultStrategy = editData?.isDefault === 1;

    useEffect(() => {
        if (!open) return;
        setStep(0);
        if (editData) {
            form.setFieldsValue({
                name: editData.name,
                remark: editData.remark,
                releaseArea: isDefaultStrategy ? ['1', '2'] : (editData.releaseArea || []),
                releaseApps: editData.releaseApps || [],
                userSegments: editData.userSegments || [],
            });
        } else {
            form.resetFields();
        }
    }, [open, editData, form, isDefaultStrategy]);

    const handleNext = async () => {
        await form.validateFields(['name']);
        setStep(1);
    };

    const handleSubmit = async () => {
        if (!isDefaultStrategy) {
            const values = await form.validateFields();
            if (!values.releaseArea?.length) { form.setFields([{ name: 'releaseArea', errors: ['请至少选择一个投放区域'] }]); return; }
            if (!values.releaseApps?.length) { form.setFields([{ name: 'releaseApps', errors: ['请至少选择一个投放APP'] }]); return; }
            if (!values.userSegments?.length) { form.setFields([{ name: 'userSegments', errors: ['请至少选择一个投放用户分群'] }]); return; }
            onSuccess({
                name: values.name,
                remark: values.remark,
                releaseArea: values.releaseArea,
                releaseApps: values.releaseApps,
                releaseAppNames: values.releaseApps.map(code => MALL_APPS.find(a => a.appCode === code)?.appName).filter(Boolean),
                userSegments: values.userSegments,
                segmentNames: values.userSegments.map(id => MALL_USER_SEGMENTS.find(s => s.id === id)?.name).filter(Boolean),
            });
        } else {
            const values = await form.validateFields(['name']);
            onSuccess({ name: values.name, remark: values.remark });
        }
    };

    return (
        <Drawer
            title={isEdit ? '编辑策略' : '添加策略'}
            width={600}
            open={open}
            onClose={onCancel}
            destroyOnClose
            maskClosable={false}
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    {step === 1 && <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(0)}>上一步</Button>}
                    <Button icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                    {step === 0
                        ? <Button type="primary" icon={<ArrowRightOutlined />} onClick={handleNext}>下一步</Button>
                        : <Button type="primary" icon={<CheckOutlined />} onClick={handleSubmit}>提交</Button>}
                </div>
            }
        >
            <div style={{ padding: '20px 40px 30px', background: '#f5f7fa', margin: '-20px -20px 20px' }}>
                <Steps current={step} size="small" items={[{ title: '基础信息' }, { title: '投放配置' }]} />
            </div>

            <Form form={form} layout="vertical">
                <div style={{ display: step === 0 ? 'block' : 'none' }}>
                    <Form.Item label="策略名称" name="name" rules={[{ required: true, message: '请输入策略名称' }]}>
                        <Input placeholder="请输入策略名称" maxLength={50} showCount />
                    </Form.Item>
                    <Form.Item label="备注" name="remark">
                        <Input.TextArea placeholder="请输入备注信息" rows={6} maxLength={200} showCount />
                    </Form.Item>
                </div>

                <div style={{ display: step === 1 ? 'block' : 'none' }}>
                    {isDefaultStrategy && <Alert type="info" showIcon message="默认策略的投放配置不可编辑" style={{ marginBottom: 16 }} />}

                    <Form.Item label="投放区域" name="releaseArea">
                        <Checkbox.Group options={AREA_OPTIONS} disabled={isDefaultStrategy} style={checkboxGroupStyle} />
                    </Form.Item>

                    <Form.Item label="投放APP" name="releaseApps">
                        <Checkbox.Group disabled={isDefaultStrategy} style={checkboxGroupStyle}>
                            {MALL_APPS.map(app => (
                                <Checkbox key={app.appCode} value={app.appCode}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginLeft: 8 }}>
                                        <span>{app.appName}</span>
                                        <span style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{app.appCode}</span>
                                    </div>
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Form.Item>

                    <Form.Item label="投放用户分群" name="userSegments">
                        <Checkbox.Group disabled={isDefaultStrategy} style={checkboxGroupStyle}>
                            {MALL_USER_SEGMENTS.map(s => (
                                <Checkbox key={s.id} value={s.id}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginLeft: 8 }}>
                                        <span>{s.name}</span>
                                        <span style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{s.id}</span>
                                    </div>
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Form.Item>
                </div>
            </Form>
        </Drawer>
    );
};

export default MallAddStrategyDrawer;
